import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalculator, FaFileAlt, FaCog, FaMagic, FaTrash, FaEye, FaEyeSlash, FaPercent, FaMoneyBillWave, FaChartLine, FaInfoCircle, FaStickyNote, FaLightbulb, FaCopy, FaUndo, FaRedo, FaBold, FaItalic, FaListUl, FaListOl } from 'react-icons/fa';

interface SummaryTabProps {
  taxRate: number;
  discount: number;
  notes: string;
  paymentTerms: string;
  onChange: (field: string, value: string | number) => void;
  onGenerateDemo?: () => void;
  onClearAll?: () => void;
  t?: Record<string, unknown>;
  subtotal?: number;
  currency?: string;
}

const SummaryTab: React.FC<SummaryTabProps> = ({
  taxRate,
  discount,
  notes,
  paymentTerms,
  onChange,
  onGenerateDemo,
  onClearAll,
  t,
  subtotal = 0,
  currency = 'SAR'
}) => {
  const [showCalculations, setShowCalculations] = useState(true);
  const [showNotesPreview, setShowNotesPreview] = useState(false);
  const [notesHistory, setNotesHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const calculations = useMemo(() => {
    const taxAmount = (subtotal * taxRate) / 100;
    const totalBeforeDiscount = subtotal + taxAmount;
    const finalTotal = totalBeforeDiscount - discount;
    
    return {
      subtotal,
      taxAmount,
      totalBeforeDiscount,
      discount,
      finalTotal,
      taxPercentage: subtotal > 0 ? ((taxAmount / subtotal) * 100) : 0,
      discountPercentage: subtotal > 0 ? ((discount / subtotal) * 100) : 0
    };
  }, [subtotal, taxRate, discount]);

  const taxTemplates = [
    { name: 'ضريبة القيمة المضافة السعودية', rate: 15, description: 'الضريبة القياسية في المملكة' },
    { name: 'بدون ضريبة', rate: 0, description: 'فواتير معفاة من الضريبة' },
    { name: 'ضريبة مخفضة', rate: 5, description: 'للبضائع والخدمات المخفضة' },
    { name: 'ضريبة صفرية', rate: 0, description: 'للتجارة الخارجية' }
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // قوالب الملاحظات السريعة
  const notesTemplates = [
    {
      title: 'شكر وتقدير',
      content: 'شكراً لثقتكم في خدماتنا. نلتزم بتقديم أفضل الحلول وأعلى مستويات الجودة.',
      icon: '🙏',
      category: 'general'
    },
    {
      title: 'شروط خاصة',
      content: 'هذه الفاتورة صالحة لمدة 30 يوم من تاريخ الإصدار. يرجى مراجعة الشروط والأحكام.',
      icon: '📋',
      category: 'terms'
    },
    {
      title: 'معلومات الشحن',
      content: 'سيتم الشحن خلال 3-5 أيام عمل. سيتم إرسال رقم التتبع عبر البريد الإلكتروني.',
      icon: '🚚',
      category: 'shipping'
    },
    {
      title: 'ضمان الجودة',
      content: 'نضمن جودة جميع المنتجات والخدمات. في حالة وجود أي مشكلة، يرجى التواصل معنا خلال 7 أيام.',
      icon: '✅',
      category: 'warranty'
    },
    {
      title: 'خصم الكمية',
      content: 'خصم خاص للكميات الكبيرة. للحصول على عروض خاصة، يرجى التواصل مع فريق المبيعات.',
      icon: '💰',
      category: 'discount'
    },
    {
      title: 'ملاحظة مخصصة',
      content: '',
      icon: '✏️',
      category: 'custom'
    }
  ];

  // عداد الكلمات
  const wordCount = notes.trim() ? notes.trim().split(/\s+/).length : 0;
  const charCount = notes.length;

  // حفظ التاريخ
  const saveToHistory = (newNotes: string) => {
    if (newNotes !== notes) {
      const newHistory = notesHistory.slice(0, historyIndex + 1);
      newHistory.push(newNotes);
      setNotesHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };

  // التراجع
  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      onChange('notes', notesHistory[newIndex]);
    }
  };

  // إعادة
  const redo = () => {
    if (historyIndex < notesHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      onChange('notes', notesHistory[newIndex]);
    }
  };

  // نسخ الملاحظات
  const copyNotes = () => {
    navigator.clipboard.writeText(notes);
  };

  // تطبيق قالب
  const applyTemplate = (template: typeof notesTemplates[0]) => {
    if (template.category === 'custom') {
      onChange('notes', notes + '\n\n');
    } else {
      const newNotes = notes + (notes ? '\n\n' : '') + template.content;
      onChange('notes', newNotes);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* بطاقة الحسابات المحسنة والمبسطة */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-700 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <FaCalculator className="text-white text-sm" />
            </div>
            {t?.calculations || 'الحسابات والضرائب'}
          </h3>
          <button
            onClick={() => setShowCalculations(!showCalculations)}
            className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded"
          >
            {showCalculations ? <FaEyeSlash className="text-sm" /> : <FaEye className="text-sm" />}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {showCalculations && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {/* ملخص الحسابات المبسط */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-blue-200 dark:border-blue-600">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">المجموع الفرعي:</span>
                      <span className="font-semibold text-gray-800 dark:text-white">
                        {formatCurrency(calculations.subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">الضريبة ({taxRate}%):</span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">
                        {formatCurrency(calculations.taxAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">الخصم:</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        -{formatCurrency(calculations.discount)}
                      </span>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800 dark:text-white">المجموع النهائي:</span>
                        <span className="font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(calculations.finalTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* مؤشرات بصرية مبسطة */}
                  <div className="space-y-3">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">الضريبة</span>
                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{taxRate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-indigo-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(taxRate, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">الخصم</span>
                        <span className="text-xs font-bold text-green-600 dark:text-green-400">
                          {calculations.discountPercentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-emerald-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(calculations.discountPercentage, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* حقول الإدخال المبسطة */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                    <FaPercent className="text-blue-500 text-xs" />
                    {t?.taxRate || 'نسبة الضريبة'} (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={taxRate}
                    onChange={(e) => onChange('taxRate', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 text-sm"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                    <FaMoneyBillWave className="text-green-500 text-xs" />
                    {t?.discount || 'الخصم'} ({currency})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount}
                    onChange={(e) => onChange('discount', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 text-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* قوالب الضرائب المبسطة */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                  <FaChartLine className="text-purple-500 text-sm" />
                  {t?.taxTemplates || 'قوالب سريعة'}
                </h4>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {taxTemplates.map((template, index) => (
                    <button
                      key={index}
                      onClick={() => onChange('taxRate', template.rate)}
                      className={`p-2 rounded-lg border transition-all duration-200 text-left ${
                        taxRate === template.rate
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600'
                      }`}
                    >
                      <div className="font-medium text-gray-800 dark:text-white text-xs">
                        {template.name}
                      </div>
                      <div className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-1">
                        {template.rate}%
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* بطاقة ملاحظات الفاتورة المحسنة والمبسطة */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-700 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <FaStickyNote className="text-white text-sm" />
            </div>
            {t?.notes || 'ملاحظات الفاتورة'}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={copyNotes}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded"
              title="نسخ الملاحظات"
            >
              <FaCopy className="text-sm" />
            </button>
            <button
              onClick={() => setShowNotesPreview(!showNotesPreview)}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded"
              title="معاينة الملاحظات"
            >
              {showNotesPreview ? <FaEyeSlash className="text-sm" /> : <FaEye className="text-sm" />}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {/* حقل إدخال الملاحظات المبسط */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-blue-200 dark:border-blue-600">
            <textarea
              value={notes}
              onChange={(e) => {
                onChange('notes', e.target.value);
                saveToHistory(e.target.value);
              }}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200 resize-none text-sm"
              placeholder={t?.notesPlaceholder || 'أدخل ملاحظات إضافية للفاتورة...'}
            />
            
            {/* عداد الكلمات المبسط */}
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{wordCount} كلمة • {charCount} حرف</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={undo}
                  disabled={historyIndex <= 0}
                  className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="تراجع"
                >
                  <FaUndo className="text-xs" />
                </button>
                <button
                  onClick={redo}
                  disabled={historyIndex >= notesHistory.length - 1}
                  className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="إعادة"
                >
                  <FaRedo className="text-xs" />
                </button>
              </div>
            </div>
          </div>

          {/* معاينة الملاحظات المبسطة */}
          <AnimatePresence mode="wait">
            {showNotesPreview && notes && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600"
              >
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {notes}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* قوالب الملاحظات المبسطة */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
            <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
              <FaLightbulb className="text-yellow-500 text-sm" />
              {t?.notesTemplates || 'قوالب سريعة'}
            </h4>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {notesTemplates.slice(0, 5).map((template, index) => (
                <button
                  key={index}
                  onClick={() => applyTemplate(template)}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 text-left bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-sm">{template.icon}</span>
                    <div className="font-medium text-gray-800 dark:text-white text-xs">
                      {template.title}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FaCog className="text-blue-600" />
          {t?.paymentTerms || 'شروط الدفع'}
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t?.paymentTermsLabel || 'شروط الدفع'}
          </label>
          <textarea
            value={paymentTerms}
            onChange={(e) => onChange('paymentTerms', e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder={t?.paymentTermsPlaceholder || 'أدخل شروط الدفع المطبقة'}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t?.paymentTermsHelp || 'حدد شروط الدفع والشروط الخاصة بالفاتورة'}
          </p>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h4 className="text-md font-bold text-gray-800 dark:text-white mb-4">
          {t?.quickTemplates || 'قوالب سريعة لشروط الدفع'}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={() => onChange('paymentTerms', 'الدفع خلال 30 يوم من تاريخ الفاتورة')}
            className="text-left p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            <div className="font-medium text-gray-800 dark:text-white">
              صافي 30 يوم
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              الدفع خلال 30 يوم من تاريخ الفاتورة
            </div>
          </button>
          
          <button
            onClick={() => onChange('paymentTerms', 'الدفع خلال 15 يوم من تاريخ الفاتورة')}
            className="text-left p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            <div className="font-medium text-gray-800 dark:text-white">
              صافي 15 يوم
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              الدفع خلال 15 يوم من تاريخ الفاتورة
            </div>
          </button>
          
          <button
            onClick={() => onChange('paymentTerms', 'الدفع مطلوب عند استلام الفاتورة')}
            className="text-left p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            <div className="font-medium text-gray-800 dark:text-white">
              الدفع عند الاستلام
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              الدفع مطلوب عند استلام الفاتورة
            </div>
          </button>
          
          <button
            onClick={() => onChange('paymentTerms', 'أدخل شروط الدفع الخاصة بك')}
            className="text-left p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
          >
            <div className="font-medium text-gray-800 dark:text-white">
              شروط مخصصة
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              أدخل شروط الدفع الخاصة بك
            </div>
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-700">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FaCog className="text-blue-600" />
          إدارة البيانات
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={onGenerateDemo}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            <FaMagic />
            {t?.generateDemo || 'توليد بيانات تجريبية'}
          </button>
          
          <button
            onClick={onClearAll}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            <FaTrash />
            {t?.clearData || 'مسح جميع البيانات'}
          </button>
        </div>
        
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                {t?.helpfulTips || 'نصائح مفيدة'}
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t?.helpfulTipsText || 'استخدم توليد بيانات تجريبية لملء الفاتورة ببيانات نموذجية، أو مسح جميع البيانات لبدء فاتورة جديدة من الصفر.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SummaryTab; 